import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import axios from "axios";
import { Layout } from "../../Components/layout";
import {
  getCoordsToLocalStorage,
  getHashToLocalStorage,
} from "../../Utils/local-storage";
import { useBiometricValidation } from "../../Contexts/biometric-validation";
import {
  Accordion,
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { cnpjCpfMask } from "../../Utils/ValidationsMasks";

import "./styles.css";
import moment from "moment";

export function BiometriaTermos() {
  const [loading, setLoading] = useState(false);
  const [showAccordion, setShowAccordion] = useState(false);
  const [arrayPermissoes, setArrayPermissoes] = useState({
    armazenamento_foto_validacao_cnh: false,
    exibicao_foto_historico_pesquisas: false,
    uso_dados_informativos: false,
    aceite_flex_consulta: false,
    aceite_transportadora: false,
  });

  const navigate = useNavigate();
  const {
    biometricInfo,
    biometricInfoLoading,
    onGetBiometricInfo,
    acceptTermsLoading,
    onAcceptTerms,
  } = useBiometricValidation();

  const hash = getHashToLocalStorage();
  const coords = getCoordsToLocalStorage();

  const allAccepted = Object.values(arrayPermissoes).every(Boolean);

  const handleSelectAll = (checked) => {
    setArrayPermissoes({
      armazenamento_foto_validacao_cnh: checked,
      exibicao_foto_historico_pesquisas: checked,
      uso_dados_informativos: checked,
      aceite_flex_consulta: checked,
      aceite_transportadora: checked,
    });
  };

  const togglePermission = (key) => {
    setArrayPermissoes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = async () => {
    if (coords) {
      setLoading(true);

      const resultCoords = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?lat=${coords?.lat}&lon=${coords?.lng}&format=json`,
      );

      if (resultCoords) {
        const geoCoords = {
          endereco: resultCoords.data?.display_name || "",
          cidade:
            resultCoords.data?.address?.city ||
            resultCoords.data?.address?.town ||
            resultCoords.data?.address?.village ||
            "",
          uf: resultCoords.data?.address?.state || "",
        };
        try {
          const result = await onAcceptTerms({
            hash,
            aceito_termo: allAccepted,
            geo_localizacao: {
              endereco: geoCoords?.endereco,
              latitude: coords?.lat,
              longitude: coords?.lng,
              cidade: geoCoords?.cidade,
              uf: geoCoords?.uf,
            },
          });

          if (result && [200, 201, 204].includes(result.status)) {
            navigate("/biometria/instrucoes");
          }
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (hash) {
      onGetBiometricInfo({ hash });
    }
  }, [hash, onGetBiometricInfo]);

  if (biometricInfoLoading) {
    return (
      <Layout>
        <div className="d-flex align-items-center justify-content-center gap-2">
          <Spinner
            animation="border"
            role="status"
            size="sm"
            style={{ color: "var(--theme-color" }}
          />
          <p className="text-muted fw-bold m-0">Carregando termos...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout className={"card_termo"}>
      <Row className="d-flex flex-column align-items-center">
        <header className="d-flex flex-column align-items-center text-center mb-4">
          <Alert className="icon_container">
            <ShieldCheck className="icon_style" />
          </Alert>
          <h1 className="title">Termo de Consentimento</h1>
          <p className="subtitle">Biometria Facial</p>
        </header>

        {!biometricInfo?.isMaster && (
          <Col className="border p-4 mb-4 card_info d-flex flex-column gap-1">
            <p className="info_label text-uppercase">
              Empresa Solicitante:{" "}
              <strong className="info_text text-truncate">
                {biometricInfo?.nome_cliente}
              </strong>
            </p>
            <p className="info_label m-0">
              CNPJ:{" "}
              <strong className="info_text">
                {cnpjCpfMask(biometricInfo?.cnpj_cliente)}
              </strong>
            </p>
          </Col>
        )}

        <Col className="border card_info mb-4">
          <div className="termos_container">
            <div style={{ marginTop: "5px" }}>
              <p style={{ textAlign: "justify" }}>
                Eu,{" "}
                <strong className="text-uppercase">
                  {biometricInfo?.nome_motorista}
                </strong>
                , inscrito no CPF nº {biometricInfo?.cpf_motorista}, declaro que
                fui orientado(a) de forma clara sobre o tratamento de Dados
                Pessoais pelo FLEX CONSULTA (CASIMIRO & MIRANDA SERVICOS LTDA,
                inscrito no CNPJ 42.520.419/0001-14), conforme as disposições
                abaixo:
              </p>
            </div>
            <div>
              <h4 className="item_termos">1. Autorização</h4>
              <p style={{ textAlign: "justify" }}>
                Em observância à Lei nº. 13.709/1833 – Lei Geral de Proteção de
                Dados Pessoais e demais normativas aplicáveis sobre proteção de
                Dados Pessoais, manifesto-me de forma informada, livre, expressa
                e consciente, no sentido de autorizar o FLEX CONSULTA a realizar
                o tratamento de meus Dados Pessoais para as finalidades e de
                acordo com as condições aqui estabelecidas.
              </p>
            </div>
            <div>
              <h4 className="item_termos">2. Finalidades do tratamento</h4>
              <p style={{ textAlign: "justify" }}>
                Os meus Dados Pessoais poderão ser utilizados pelo FLEX CONSULTA
                para:
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.1 Cumprir as obrigações contratuais, legais e regulatórias do
                SISTEMA FLEX, em razão de suas atividades;
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.2. Execução de seus programas e prestação de serviços;
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.3. Oferecer produtos e serviços que sejam do meu interesse;
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.4. Compartilhar com os clientes Flex, histórico de viagens
                realizadas junto a transportadora para qual houve prestação de
                serviço que é cliente Flex.
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.5. Realizar a comunicação oficial pelo SISTEMA FLEX ou por
                seus prestadores de serviço, por meio de canais de comunicação
                (telefone, e-mail, SMS e WhatsApp);
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.6. Armazenar a foto coletada durante a validação de biometria
                facial para fins de comprovação e histórico de consulta
                realizada garantindo a veracidade da consulta;
              </p>
              <p
                style={{
                  textAlign: "justify",
                  paddingLeft: 10,
                  marginBottom: "-1px",
                }}
              >
                2.7. Armazenar meu número de telefone, utilizado durante a
                validação de biometria facial para garantir a veracidade da
                consulta;
              </p>
              <p style={{ textAlign: "justify", paddingLeft: 10 }}>
                2.8. Armazenar número de registro e categoria da CNH, utilizado
                para validação de biometria facial;
              </p>
              <p style={{ textAlign: "justify" }}>
                Estou ciente que a FLEX CONSULTA poderá compartilhar os meus
                Dados Pessoais com seus parceiros e demais prestadores de
                serviços, restringindo-se às funções e atividades por cada um
                desempenhadas e em aderência às finalidades acima estabelecidas.
              </p>
            </div>
            <div>
              <h4 className="item_termos">3. Confidencialidade</h4>
              <p style={{ textAlign: "justify" }}>
                Estou ciente do compromisso assumido pela FLEX CONSULTA de
                tratar os meus Dados Pessoais de forma sigilosa e confidencial,
                mantendo-os em ambiente seguro e não sendo utilizados para
                qualquer fim que não os descritos acima.
              </p>
            </div>
            <div>
              <h4 className="item_termos">
                4. Governança e Segurança dos Dados
              </h4>
              <p style={{ textAlign: "justify", paddingLeft: "10px" }}>
                4.1. A CONTRATADA compromete-se a adotar medidas, ferramentas e
                tecnologias necessárias para garantir a segurança dos dados e
                cumprir com suas obrigações, sempre considerando o estado da
                técnica disponível e as bases contratuais originais.
              </p>
              <p style={{ textAlign: "justify", paddingLeft: "10px" }}>
                4.2. A CONTRATADA manterá registro das operações de tratamento
                dos dados pessoais que realizar, implementando medidas técnicas
                e organizacionais necessárias para proteger os dados contra a
                destruição, acidental ou ilícita, a perda, a alteração, a
                comunicação ou difusão ou o acesso não autorizado, além de
                garantir que os ambientes (seja ele físico ou lógico) utilizados
                por ela para o tratamento de dados pessoais sejam estruturados
                de forma a atender os requisitos de segurança, previstos em boas
                práticas e de governança e aos princípios gerais previstos em
                Lei e às demais normas regulamentares aplicáveis.
              </p>
              <p style={{ textAlign: "justify", paddingLeft: "10px" }}>
                4.3. Em que pese os melhores esforços da CONTRATADA em proteger
                as informações, é dever de cada CONTRATANTE a responsabilidade
                de garantir e assegurar que seus computadores se encontrem
                adequadamente protegido contra softwares nocivos, como vírus,
                spywares, adwares, acesso remoto não autorizado, dentre outras
                atividades e programas maliciosos no meio digital.
              </p>
              <p style={{ textAlign: "justify", paddingLeft: "10px" }}>
                4.4. A CONTRATANTE declara estar ciente de que é responsável
                pela adoção de medidas de segurança adequadas, como, por
                exemplo, a configuração segura de seu navegador, utilização de
                programa antivírus atualizado, firewall, IPS, WAF (Web
                Application Firewall), atualizar seus softwares dos servidores e
                nos computadores, criar utilizar programas desenvolvidos com
                técnicas que garantam segurança, não utilização de software de
                origem ilegal ou duvidosa, utilizar senhas seguras e complexas,
                e demais melhores práticas de segurança, dentre outros, sem os
                quais o risco de os dados pessoais e senhas serem acessados por
                terceiros, sem autorização para tal, é consideravelmente maior.
              </p>
              <p style={{ textAlign: "justify", paddingLeft: "10px" }}>
                4.5. Diante disso, a CONTRATANTE exime expressamente a
                CONTRATADA de quaisquer responsabilidades por eventuais
                incidentes de vazamento de dados e dos danos e/ou prejuízos
                decorrentes de eventual invasão no Portal de Acesso utilizado
                pela CONTRATANTE para acessar os servidores da CONTRATADA, bem
                como de demais falhas relacionadas à segurança dos dados
                coletados, salvo na hipótese de dolo ou culpa da CONTRATADA.
              </p>
            </div>
            <div>
              <h4 className="item_termos">5. Revogação</h4>
              <p style={{ textAlign: "justify" }}>
                Estou ciente que, a qualquer tempo, posso retirar o
                consentimento ora fornecido, hipótese em que as atividades
                desenvolvidas pela FLEX CONSULTA, no âmbito de nossa relação,
                poderão restar prejudicadas.
              </p>
              <p style={{ textAlign: "justify" }}>
                Declaro e concordo que os meus Dados Pessoais poderão ser
                armazenados em até 5 anos, mesmo após o término do tratamento –
                inclusive após a revogação do consentimento –, (i) para
                cumprimento de obrigação legal ou regulatória pela FLEX CONSULTA
                ou (ii) desde que tornados anônimos.
              </p>
            </div>
            <div>
              <h4 className="item_termos">6. Comprometimento a LGPD</h4>
              <p style={{ textAlign: "justify" }}>
                Por meio do presente instrumento, a FLEX CONSULTA, seja na
                condição de controladora ou operadora de dados, em decorrência
                da relação contratual havida com a CONTRATANTE, reitera seu
                compromisso com a estrita observância dos princípios norteadores
                da Lei Geral de Proteção de Dados Pessoais (Lei nº. 13.709/18),
                reafirmando tudo o quanto exposto em sua Política de Privacidade
                e Proteção de Dados, à qual a CONTRATANTE declara ter tido
                acesso e concordar com o seu inteiro teor.
              </p>
            </div>
            <div>
              <h4 className="item_termos">7. Canal de Atendimento</h4>
              <p style={{ textAlign: "justify" }}>
                Estou ciente que posso utilizar o canal de atendimento à LGPD da
                FLEX CONSULTA, por meio do endereço dpo@flexconsulta.com.br,
                para tirar dúvidas e/ou realizar solicitações relacionadas ao
                tratamento dos meus Dados Pessoais.
              </p>
              <p style={{ textAlign: "justify" }}>
                Por fim, declaro ter lido e ter sido suficientemente informado
                sobre o conteúdo deste Termo e concordo com o tratamento dos
                meus Dados Pessoais aqui descrito de forma livre e esclarecida,
                em observância à Lei Geral de Proteção de Dados e às demais
                normativas sobre proteção de Dados Pessoais aplicáveis.
              </p>
            </div>
            <div>
              <h4 className="item_termos">8. Canal de Comunicação</h4>
              <p style={{ textAlign: "justify" }}>
                Manifesto-me de forma informada, livre, expressa e consciente,
                no sentido de autorizar o SISTEMA FLEX CONSULTA a realizar
                contato comigo através dos seguintes canais:
              </p>
              {biometricInfo?.email_dpo_empresa && (
                <p style={{ textAlign: "justify", marginBottom: "3px" }}>
                  {biometricInfo?.email_dpo_empresa};
                </p>
              )}
              <p style={{ textAlign: "justify" }}>App Flex Consulta.</p>
            </div>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <label
                style={{ fontSize: "13px", color: "black", fontWeight: "bold" }}
              >
                {moment(new Date()).format("DD/MM/YYYY")}
              </label>
            </div>
          </div>
        </Col>

        <Col className="mb-4 d-flex flex-column gap-4">
          <Form.Group
            className="d-flex align-items-center gap-2 border card_termos"
            controlId="accept_all"
          >
            <Form.Check
              type="checkbox"
              checked={allAccepted}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="custom-checkbox"
              size="lg"
            />

            <Form.Label className="check_label m-0">
              Aceitar Todos os Termos
            </Form.Label>
          </Form.Group>

          <Accordion className="card_info">
            <Accordion.Item eventKey="0" className="border-0 card_info">
              <Accordion.Header
                onClick={() => setShowAccordion(!showAccordion)}
                className="accordion_button border-0"
              >
                MAIS DETALHES DO ACEITE
              </Accordion.Header>

              <Accordion.Body
                className={`accordion_content p-4 border-0 border-top`}
              >
                <div className="d-flex flex-column gap-3">
                  <PermissionItem
                    label="Armazenar foto da Validação do CNH"
                    id="armazenamento_foto_validacao_cnh"
                    checked={arrayPermissoes.armazenamento_foto_validacao_cnh}
                    onChange={() =>
                      togglePermission("armazenamento_foto_validacao_cnh")
                    }
                  />
                  <PermissionItem
                    label="Exibição foto na pesquisa de viagens realizadas"
                    checked={arrayPermissoes.exibicao_foto_historico_pesquisas}
                    id="exibicao_foto_historico_pesquisas"
                    onChange={() =>
                      togglePermission("exibicao_foto_historico_pesquisas")
                    }
                  />
                  <PermissionItem
                    label="Coleta de dados (Nome, CPF, Celular e N° de CNH)"
                    checked={arrayPermissoes.uso_dados_informativos}
                    id="uso_dados_informativos"
                    onChange={() => togglePermission("uso_dados_informativos")}
                  />
                  <PermissionItem
                    label="Aceito termos FLEX CONSULTA"
                    checked={arrayPermissoes.aceite_flex_consulta}
                    id="aceite_flex_consulta"
                    onChange={() => togglePermission("aceite_flex_consulta")}
                  />
                  <PermissionItem
                    label={`Aceito termos ${biometricInfo?.nome_cliente ? biometricInfo?.nome_cliente : "Flex Consulta"}`}
                    checked={arrayPermissoes.aceite_transportadora}
                    id="aceite_transportadora"
                    onChange={() => togglePermission("aceite_transportadora")}
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>

        <Col className="d-flex flex-column gap-3">
          <Button
            disabled={!allAccepted || acceptTermsLoading || loading}
            onClick={handleConfirm}
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
          >
            {acceptTermsLoading || loading ? (
              <Spinner animation="border" role="status" variant="light" />
            ) : (
              "Confirmar e Continuar"
            )}
          </Button>
          <Button
            disabled={acceptTermsLoading || loading}
            onClick={() => navigate("/biometria/cancelar")}
            variant="danger"
            className="py-2 d-flex align-items-center justify-content-center gap-2"
            size="lg"
          >
            Cancelar Processo
          </Button>
        </Col>
      </Row>
    </Layout>
  );
}

function PermissionItem({ label, checked, onChange, id }) {
  return (
    <Form.Group className="d-flex align-items-center gap-2" controlId={id}>
      <Form.Check
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="custom-checkbox"
        size="lg"
      />

      <Form.Label className="m-0 check_label_details text-muted">
        {label}
      </Form.Label>
    </Form.Group>
  );
}
